import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    FormControl,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    flexRender,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table';
import Axios from 'axios';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useMemo, useState } from 'react';
import { Input } from '@mui/material';
import getCookie from '../../utils/getCookies';

dayjs.extend(utc);
dayjs.extend(timezone);

export default function CompletedTaskDashboard({ tasks, isLoading, errorMessage, action, setAction, actionCompleted, setActionCompleted }) {
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [inquiryTypeFilter, setInquiryTypeFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [sorting, setSorting] = useState([{ id: 'requested_at', desc: false }]);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(Date.now());
    const [rollbackTask, setRollbackTask] = useState(null);

    const columns = useMemo(
        () => [
            {
                header: 'Task ID',
                accessorKey: 'id',
            },
            {
                header: 'Type',
                cell: ({ row }) => {
                    const { memo_id, is_info_mail } = row.original;
                    if (memo_id !== null && is_info_mail === false) {
                        return '재문의';
                    } else if (memo_id !== null && is_info_mail === true) {
                        return '신규문의';
                    }
                    return '-';
                },
            },
            {
                header: 'Consultation Number',
                accessorKey: 'student.consultation_number',
                cell: ({ row }) => (
                    <Button
                        onClick={() => handleClickOpen(row)}
                        sx={{ textTransform: 'none' }}
                        color="primary"
                    >
                        {row.original.student?.consultation_number}
                    </Button>
                ),
            },
            {
                header: 'Email 1 Schedule',
                accessorKey: 'first_email_sent',
                cell: ({ getValue, row }) => {
                    const requestDate = dayjs(row.original.requested_at);
                    const firstEmailSent = getValue();
                    
                    let scheduleDate;
                    if (requestDate.tz('America/New_York').hour() >= 22 && requestDate.tz('America/New_York').minute() >= 30) {
                        scheduleDate = requestDate.tz('America/New_York').add(1, 'day').hour(22).minute(30).second(0);
                    } else {
                        scheduleDate = requestDate.tz('America/New_York').hour(22).minute(30).second(0);
                    }
                    
                    const now = dayjs();
                    const diffMinutes = scheduleDate.diff(now, 'minute');
                    
                    let timeLeft;
                    if (firstEmailSent) {
                        timeLeft = ' ✅';
                    } else if (diffMinutes < 0) {
                        timeLeft = ' ❌';
                    } else if (diffMinutes > 1440) {
                        const days = Math.floor(diffMinutes / 1440);
                        timeLeft = ` (${days}d remaining)`;
                    } else {
                        const hoursLeft = Math.floor(diffMinutes / 60);
                        const minutesLeft = diffMinutes % 60;
                        timeLeft = ` (${hoursLeft}h ${minutesLeft}m remaining)`;
                    }
    
                    return (firstEmailSent ? firstEmailSent : scheduleDate.tz('America/New_York').format('MM/DD/YYYY hh:mm A')) + timeLeft;
                },
            },
            {
                header: 'Email 2 Schedule',
                accessorKey: 'second_email_sent',
                cell: ({ getValue, row }) => {
                    const requestDate = dayjs(row.original.requested_at);
                    const scheduleDate = getValue()
                        ? dayjs(getValue())
                        : requestDate.tz('America/New_York').add(1, 'week').hour(22).minute(30).second(0);
                    
                    const now = dayjs();
                    const diffMinutes = scheduleDate.diff(now, 'minute');
                    
                    let timeLeft;
                    if (diffMinutes > 0) {
                        if (diffMinutes > 1440) {
                            const days = Math.floor(diffMinutes / 1440);
                            timeLeft = ` (${days}d remaining)`;
                        } else {
                            const hoursLeft = Math.floor(diffMinutes / 60);
                            const minutesLeft = diffMinutes % 60;
                            timeLeft = ` (${hoursLeft}h ${minutesLeft}m remaining)`;
                        }
                    } else {
                        timeLeft = ' (passed)';
                    }
    
                    return (getValue() ? getValue() : scheduleDate.tz('America/New_York').format('MM/DD/YYYY hh:mm A')) + timeLeft;
                },
            },
            {
                header: 'Completed',
                accessorKey: 'completed_by',
            },
            {
                header: 'Request Date',
                accessorKey: 'requested_at',
                cell: ({ getValue }) => {
                    const requestDate = dayjs(getValue());
                    return requestDate.tz('America/New_York').format('MM/DD/YYYY');
                },
            },
            {
                header: 'Completed Date',
                accessorKey: 'completed_at',
                cell: ({ getValue }) => {
                    const completedDate = dayjs(getValue());
                    return completedDate.tz('America/New_York').format('MM/DD/YYYY');
                },
            },
            {
                header: 'Actions',
                cell: ({ row }) => (
                    <Button 
                        variant="contained" 
                        color="warning"
                        onClick={() => handleRollbackClick(row.original)}
                    >
                        Rollback
                    </Button>
                ),
            },
        ],
        []
    );
    const table = useReactTable({
        data: tasks,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        filterFns: {
            custom: () => true,
        },
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
    });


    const handleClickOpen = (row) => {
        setSelectedStudent(row.original);
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setSelectedStudent(null);
    };

    const handleRollback = async (row) => {
        try {
            const token = getCookie('_auth');
            const res = await Axios.patch(`${process.env.REACT_APP_URL}/api/v1/tasks/rollback/${row.id}/${row.student_id}`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            })
            if(res.status === 200){
                setAction(prev => prev + 1);
                setActionCompleted(prev => prev + 1);
            }
        } catch (error) {
            console.error('[Rollback] Error:', error);
            setError('Failed to rollback task');
        }
    }
    
    const StudentDialog = () => {
        const isReInquiry = selectedStudent?.memo_id !== null && selectedStudent?.info_mail_id === null;

        return (
            <Dialog
                open={openDialog}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
            >
                <DialogContent sx={{ pt: 4 }}>
                    {selectedStudent && (
                        <List sx={{ '& .MuiListItem-root': { flexDirection: 'column', alignItems: 'flex-start' } }}>
                            <ListItem>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                                    Student Details
                                </Typography>
                                <Box sx={{ width: '100%' }}>
                                    <Typography component="span" display="block" sx={{ mb: 1.5 }}>
                                        Name: {
                                            selectedStudent.student?.first_name || selectedStudent.student?.last_name || selectedStudent.student?.korean_name
                                                ? `${selectedStudent.student?.first_name || ''} ${selectedStudent.student?.korean_name ? `(${selectedStudent.student?.korean_name})` : ''} ${selectedStudent.student?.last_name || ''}`
                                                : '(null)'
                                        }
                                    </Typography>
                                    <Typography component="span" display="block" sx={{ mb: 1.5 }}>
                                        Parent Email: {selectedStudent.student?.parent_email}
                                    </Typography>
                                    <Typography component="span" display="block" sx={{ mb: 1.5 }}>
                                        Parent Phone: {selectedStudent.student?.parent_phone}
                                    </Typography>
                                </Box>
                            </ListItem>
                            {isReInquiry && (
                                <>
                                    <Divider sx={{ my: 3 }} />
                                    <ListItem>
                                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                                            Memo
                                        </Typography>
                                        <Box sx={{ width: '100%' }}>
                                            {selectedStudent?.memo ? (
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                        {selectedStudent.memo.title}
                                                    </Typography>
                                                    <Typography sx={{ color: 'text.secondary', mb: 2 }}>
                                                        {dayjs(selectedStudent.memo.createdAt).format('MM/DD/YYYY hh:mm A')}
                                                    </Typography>
                                                    <Typography 
                                                        sx={{ 
                                                            whiteSpace: 'pre-line',
                                                            backgroundColor: 'grey.50',
                                                            p: 2,
                                                            borderRadius: 1
                                                        }}
                                                    >
                                                        {selectedStudent.memo.content}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                "No memo available"
                                            )}
                                        </Box>
                                    </ListItem>
                                </>
                            )}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    };

    const filteredRows = table.getRowModel().rows.filter(row => {
        if (search) {
            const consultationNumber = String(row.original.student?.consultation_number || '');
            if (!consultationNumber.toLowerCase().includes(search.toLowerCase())) {
                return false;
            }
        }

        if (inquiryTypeFilter !== 'all') {
            const isReInquiry = row.original.memo_id !== null && row.original.info_mail_id === null;
            const isNewInquiry = row.original.info_mail_id !== null && row.original.memo_id === null;
            if (inquiryTypeFilter === '재문의' && !isReInquiry) return false;
            if (inquiryTypeFilter === '신규문의' && !isNewInquiry) return false;
        }
        return true;
    });

    const handleRollbackClick = (row) => {
        setRollbackTask(row);
    };

    const handleRollbackConfirm = async () => {
        try {
            await handleRollback(rollbackTask);
            setRollbackTask(null);
        } catch (error) {
            console.error('[Rollback] Error:', error);
            setError('Failed to rollback task');
        }
    };

    const handleRollbackCancel = () => {
        setRollbackTask(null);
    };

    const RollbackConfirmationDialog = () => (
        <Dialog
            open={rollbackTask !== null}
            onClose={handleRollbackCancel}
            maxWidth="sm"
            fullWidth
        >
            <DialogContent sx={{ pt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Confirm Rollback
                </Typography>
                <Typography>
                    Are you sure you want to rollback task #{rollbackTask?.id} for consultation #{rollbackTask?.student?.consultation_number}?
                </Typography>
                <Typography>
                    Task was previously completed by {rollbackTask?.completed_by} at {dayjs(rollbackTask?.completed_at).format('MM/DD/YYYY hh:mm A')}
                </Typography>
                <Typography color="warning.main" sx={{ mt: 2 }}>
                    This action will move the task back to the scheduled tasks list.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleRollbackCancel} color="inherit">
                    Cancel
                </Button>
                <Button onClick={handleRollbackConfirm} color="warning" variant="contained">
                    Rollback
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Inquiry Type</InputLabel>
                    <Select
                        value={inquiryTypeFilter}
                        onChange={(e) => setInquiryTypeFilter(e.target.value)}
                        label="Inquiry Type"
                    >
                        <MenuItem value="all">전체</MenuItem>
                        <MenuItem value="재문의">재문의</MenuItem>
                        <MenuItem value="신규문의">신규문의</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 300 }}>
                    <InputLabel>Search by consultation number</InputLabel>
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Enter consultation number"
                        fullWidth
                    />
                </FormControl>

                <Button 
                    variant="outlined"
                    onClick={() => {
                        setSorting(prev => [{
                            id: 'requested_at',
                            desc: !prev[0]?.desc
                        }]);
                    }}
                >
                    Sort Date {!sorting[0]?.desc ? '↑' : '↓'}
                </Button>
            </Stack>

            {isLoading && tasks.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper sx={{ 
                    width: '100%', 
                    overflow: 'visible',
                    mb: 6,
                    boxShadow: 3,
                    '& .MuiTableHead-root': {
                        '& .MuiTableCell-root': {
                            color: 'black',
                            fontWeight: 'bold',
                        }
                    }
                }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <TableCell 
                                                key={header.id}
                                                sx={{ fontSize: '1rem' }}
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHead>
                            <TableBody>
                                {filteredRows.map(row => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map(cell => (
                                            <TableCell 
                                                key={cell.id}
                                                sx={{ fontSize: '1rem' }}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
            <StudentDialog />
            <RollbackConfirmationDialog />
        </>
    );
}